import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Bell, Plus, Send, Trash2, Info, AlertTriangle, Megaphone, Gift, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const typeConfig = {
  info: { label: 'Informação', icon: Info, color: 'bg-blue-500', textColor: 'text-blue-400' },
  warning: { label: 'Aviso', icon: AlertTriangle, color: 'bg-yellow-500', textColor: 'text-yellow-400' },
  update: { label: 'Atualização', icon: Megaphone, color: 'bg-purple-500', textColor: 'text-purple-400' },
  promo: { label: 'Promoção', icon: Gift, color: 'bg-green-500', textColor: 'text-green-400' }
};

const AdminNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [deleteNotification, setDeleteNotification] = useState(null);
  const [sending, setSending] = useState(false);
  
  const [form, setForm] = useState({
    title: '',
    message: '',
    type: 'info',
    target: 'all'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [notifRes, storesRes] = await Promise.all([
        axios.get(`${API}/admin/notifications`),
        axios.get(`${API}/admin/stores`)
      ]);
      setNotifications(notifRes.data);
      setStores(storesRes.data);
    } catch (e) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.title || !form.message) {
      toast.error('Preencha todos os campos');
      return;
    }

    try {
      setSending(true);
      await axios.post(`${API}/admin/notifications`, form);
      toast.success('Notificação enviada!');
      setShowNew(false);
      setForm({ title: '', message: '', type: 'info', target: 'all' });
      fetchData();
    } catch (e) {
      toast.error('Erro ao enviar notificação');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteNotification) return;
    
    try {
      await axios.delete(`${API}/admin/notifications/${deleteNotification.id}`);
      toast.success('Notificação excluída');
      setDeleteNotification(null);
      fetchData();
    } catch (e) {
      toast.error('Erro ao excluir');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#ff6b00] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Notificações para Lojistas</h1>
          <p className="text-white/60">Envie avisos e novidades para os lojistas</p>
        </div>
        <Button
          onClick={() => setShowNew(true)}
          className="bg-[#ff6b00] hover:bg-[#ff8533]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Notificação
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {Object.entries(typeConfig).map(([key, config]) => {
          const Icon = config.icon;
          const count = notifications.filter(n => n.type === key).length;
          return (
            <Card key={key} className="bg-[#13131f] border-white/10">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`p-2 rounded-lg ${config.color}/20`}>
                  <Icon className={`w-5 h-5 ${config.textColor}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{count}</p>
                  <p className="text-xs text-white/60">{config.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Notifications List */}
      <Card className="bg-[#13131f] border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#ff6b00]" />
            Histórico de Notificações
          </CardTitle>
        </CardHeader>
        <CardContent>
          {notifications.length === 0 ? (
            <div className="text-center py-8">
              <Bell className="w-12 h-12 mx-auto mb-3 text-white/20" />
              <p className="text-white/60">Nenhuma notificação enviada</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications.map(notif => {
                const config = typeConfig[notif.type] || typeConfig.info;
                const Icon = config.icon;
                return (
                  <div
                    key={notif.id}
                    className="p-4 bg-[#0f0f15] rounded-lg border border-white/5"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${config.color}/20`}>
                          <Icon className={`w-4 h-4 ${config.textColor}`} />
                        </div>
                        <div>
                          <h4 className="text-white font-medium">{notif.title}</h4>
                          <p className="text-sm text-white/60 mt-1">{notif.message}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <Badge className={`${config.color} text-white text-xs`}>
                              {config.label}
                            </Badge>
                            <span className="text-xs text-white/40 flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {notif.target === 'all' ? 'Todos os lojistas' : 'Específicos'}
                            </span>
                            <span className="text-xs text-white/40">
                              {new Date(notif.created_at).toLocaleDateString('pt-BR')}
                            </span>
                            <span className="text-xs text-white/40">
                              {notif.read_by?.length || 0} visualizações
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-red-400 hover:bg-red-500/10"
                        onClick={() => setDeleteNotification(notif)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Notification Modal */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-[#13131f] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Nova Notificação</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-white/60">Título</label>
              <Input
                placeholder="Título da notificação"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="bg-[#0f0f15] border-white/10 text-white mt-1"
              />
            </div>
            <div>
              <label className="text-sm text-white/60">Tipo</label>
              <Select
                value={form.type}
                onValueChange={(v) => setForm({ ...form, type: v })}
              >
                <SelectTrigger className="bg-[#0f0f15] border-white/10 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10">
                  {Object.entries(typeConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2">
                        <config.icon className={`w-4 h-4 ${config.textColor}`} />
                        {config.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-white/60">Destinatários</label>
              <Select
                value={form.target}
                onValueChange={(v) => setForm({ ...form, target: v })}
              >
                <SelectTrigger className="bg-[#0f0f15] border-white/10 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10">
                  <SelectItem value="all">Todos os lojistas</SelectItem>
                  {stores.map(store => (
                    <SelectItem key={store.id} value={store.id}>
                      {store.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-white/60">Mensagem</label>
              <Textarea
                placeholder="Escreva sua mensagem..."
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="bg-[#0f0f15] border-white/10 text-white mt-1 min-h-[100px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNew(false)}>Cancelar</Button>
            <Button
              onClick={handleCreate}
              disabled={sending}
              className="bg-[#ff6b00] hover:bg-[#ff8533]"
            >
              <Send className="w-4 h-4 mr-2" />
              {sending ? 'Enviando...' : 'Enviar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteNotification} onOpenChange={() => setDeleteNotification(null)}>
        <AlertDialogContent className="bg-[#13131f] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Excluir Notificação</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              Tem certeza que deseja excluir esta notificação?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/10 text-white border-white/10">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-500 hover:bg-red-600"
              onClick={handleDelete}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminNotifications;
